import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import ImageUploadField from "../../components/ImageUploadField";
import { resolvePhotoUrl } from "../../lib/photo";

const empty = {
  name: "", title_tr: "", title_en: "", bio_tr: "", bio_en: "",
  expertise_tr: "", expertise_en: "",
  photo_url: "", email: "", phone: "", linkedin: "", order: 0,
};

export default function AdminTeam() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/team").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const open = (item) => { setEditing(item); setForm(item ? { ...item } : empty); };
  const close = () => { setEditing(null); setForm(empty); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing && editing.id) {
        await api.patch(`/admin/team/${editing.id}`, form);
        toast.success("Üye güncellendi");
      } else {
        await api.post("/admin/team", form);
        toast.success("Üye eklendi");
      }
      close();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hata");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/admin/team/${id}`);
    toast.success("Silindi");
    load();
  };

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="admin-team-page" className="p-10">
      <div className="flex justify-between items-end">
        <div>
          <div className="overline text-[#4A6A8F]">İçerik</div>
          <h1 className="font-serif-ap text-4xl text-[#0F2746] mt-3">Ekip Üyeleri</h1>
        </div>
        <button
          data-testid="add-team-btn"
          onClick={() => open(null)}
          className="bg-[#0F2746] text-[#F3F5F8] hover:bg-[#081A33] px-6 py-3 text-xs uppercase tracking-wider flex items-center gap-2"
        >
          <Plus size={14} /> Yeni Üye
        </button>
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((m) => (
          <div key={m.id} data-testid={`team-row-${m.id}`} className="bg-white border border-[#0F2746]/10 overflow-hidden">
            {m.photo_url && <div className="aspect-[4/3] bg-[#0F2746]/5"><img src={resolvePhotoUrl(m.photo_url)} alt={m.name} className="w-full h-full object-cover" /></div>}
            <div className="p-5">
              <div className="overline text-[10px] text-[#4A6A8F]">{m.title_tr}</div>
              <div className="font-serif-ap text-2xl text-[#0F2746] mt-1">{m.name}</div>
              <div className="text-xs text-[#3A5170] mt-2">{m.email}</div>
              <div className="mt-4 flex gap-2 justify-end">
                <button data-testid={`edit-team-${m.id}`} onClick={() => open(m)} className="text-[#0F2746] hover:text-[#C4A47C] p-2"><Pencil size={14} /></button>
                <button data-testid={`delete-team-${m.id}`} onClick={() => remove(m.id)} className="text-[#0F2746] hover:text-red-600 p-2"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-50 bg-[#081A33]/60 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white max-w-2xl w-full max-h-[90vh] overflow-auto p-8 relative">
            <button type="button" onClick={close} className="absolute top-4 right-4 text-[#0F2746]"><X size={18} /></button>
            <h2 className="font-serif-ap text-3xl text-[#0F2746]">{editing.id ? "Üyeyi Düzenle" : "Yeni Üye"}</h2>
            <div className="mt-8 grid grid-cols-2 gap-6">
              <Input label="İsim" value={form.name} onChange={onChange("name")} required />
              <Input label="Sıra" type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: parseInt(e.target.value) || 0 }))} />
              <Input label="Ünvan (TR)" value={form.title_tr} onChange={onChange("title_tr")} required />
              <Input label="Title (EN)" value={form.title_en} onChange={onChange("title_en")} required />
              <ImageUploadField
                label="Profil Fotoğrafı"
                value={form.photo_url}
                onUploaded={(url) => setForm((f) => ({ ...f, photo_url: url }))}
                onClear={() => setForm((f) => ({ ...f, photo_url: "" }))}
              />
              <Input label="E-posta" value={form.email} onChange={onChange("email")} />
              <Input label="Telefon" value={form.phone} onChange={onChange("phone")} />
              <Input label="LinkedIn" value={form.linkedin} onChange={onChange("linkedin")} />
              <Textarea label="Bio (TR)" value={form.bio_tr} onChange={onChange("bio_tr")} />
              <Textarea label="Bio (EN)" value={form.bio_en} onChange={onChange("bio_en")} />
            </div>
            <div className="mt-8 flex gap-3 justify-end">
              <button type="button" onClick={close} className="border border-[#0F2746]/30 text-[#0F2746] px-6 py-3 text-xs uppercase tracking-wider">İptal</button>
              <button type="submit" data-testid="save-team-btn" className="bg-[#0F2746] text-[#F3F5F8] hover:bg-[#081A33] px-6 py-3 text-xs uppercase tracking-wider">Kaydet</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Input({ label, ...p }) {
  return (
    <label className="block">
      <span className="overline text-[10px] text-[#4A6A8F] block mb-2">{label}</span>
      <input {...p} className="w-full bg-transparent border-b border-[#0F2746]/30 focus:border-[#0F2746] outline-none py-2 text-[#0F2746]" />
    </label>
  );
}
function Textarea({ label, ...p }) {
  return (
    <label className="block col-span-2">
      <span className="overline text-[10px] text-[#4A6A8F] block mb-2">{label}</span>
      <textarea {...p} rows={3} className="w-full bg-transparent border border-[#0F2746]/30 focus:border-[#0F2746] outline-none p-3 text-[#0F2746] resize-none" />
    </label>
  );
}
